import { useState, useEffect } from "react";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ErrorMessage from "../../components/common/ErrorMessage";
import TableComponent from "../../components/common/TableComponent";
import {
  generateNoDataValues,
  getValueInCurrency,
  encryptForUrl,
} from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { BackForeColor } from "../../components/common/BackForeColor";
import {
  getCurrencyFromSetup,
  getSettlementFilteringOptions,
  getSettlementStatusName,
  getSettlementStatusBackColor,
  getSettlementStatusForeColor,
} from "../../common/EntitiesManagement";
import LoadingPanel from "../../components/common/LoadingPanel";
import NewSettlementWizard from "./components/NewSettlementWizard";
import ActionButtons from "../../components/common/ActionButtons";
import { Print } from "@mui/icons-material";

export default function SettlementList() {
  const {
    settlementData,
    settlementError,
    pageIndexSettlement,
    setPageIndexSettlement,
    setBasicFilterOptions,
    basicFilterData,
  } = useGlobalContext();

  const [showWizard, setShowWizard] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getSettlementFilteringOptions());
  }, []);

  useEffect(() => {
    if (settlementError) {
      setErrorMessage(settlementError?.message || localize.error_unexpected);
      setShowErrorModal(true);
    }
  }, [settlementError]);

  const normalizedData = Array.isArray(settlementData)
    ? { Data: settlementData, Count: settlementData.length }
    : settlementData;

  const getColumnTitles = () => [
    localize.code,
    localize.settlement_total_settled_value,
    localize.status,
    localize.observations,
    localize.modification_date,
    localize.modified_by,
  ];

  if (!settlementData && !settlementError) {
    return <LoadingPanel />;
  }

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.settlements_title}
            subtitle={localize.settlement_description}
          />
          <SearcherAndAdd
            handleAddBtn={() => setShowWizard(true)}
            modal={
              <NewSettlementWizard show={showWizard} setShow={setShowWizard} />
            }
            printButtonMethod={() => {
              const query =
                basicFilterData?.length > 0
                  ? `?filter=${encryptForUrl(JSON.stringify(basicFilterData))}`
                  : "";
              const win = window.open(
                `/settlement/report-all${query}`,
                "_blank",
              );
              if (win) win.focus();
            }}
          />
        </CardContent>
      </Card>

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexSettlement}
            setPageIndex={setPageIndexSettlement}
            dataArray={normalizedData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          normalizedData?.Data?.length > 0
            ? normalizedData.Data.map((item) => ({
                key: item.Uuid,
                data: [
                  <ActionButtons 
                    hideEditButton
                    hideDeleteButton
                    buttons={[
                        {
                            name: localize.print,
                            action: () => {
                              if (!item?.Uuid) return;
                              const win = window.open(
                                `/settlement/${item.Uuid}/report`,
                                "_blank",
                              );
                              if (win) win.focus();
                            },
                            icon: <Print sx={{width: "16px", height: "16px"}}/>
                        }
                    ]}
                  />,
                  item?.Uuid ? (
                    <a
                      href={`/settlement/${item.Uuid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1976d2",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {item.Uuid.split("-")[0]}
                    </a>
                  ) : (
                    <ValueOrNoDataLabel data={undefined} />
                  ),
                  <ValueOrNoDataLabel
                    data={getValueInCurrency(
                      getCurrencyFromSetup(),
                      item?.totalValue ?? 0,
                    )}
                  />,
                  <BackForeColor
                    backColor={getSettlementStatusBackColor(item.status)}
                    foreColor={getSettlementStatusForeColor(item.status)}
                    text={getSettlementStatusName(item.status)}
                  />,
                  <ValueOrNoDataLabel data={item?.Observations} />,
                  <p className="textAlignedCenter">
                    {item?.ModificationDate
                      ? new Date(item.ModificationDate).toLocaleString(
                          undefined,
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          },
                        )
                      : localize.no_data}
                  </p>,
                  <ValueOrNoDataLabel data={item?.CreatedBy} />,
                ],
              }))
            : [generateNoDataValues(7)]
        }
      />
    </div>
  );
}
